import React, { createContext, useContext, useEffect, useState } from "react";
import { useCart } from "react-use-cart";
import ReactGA from "react-ga4";
import { find, isEqual } from "lodash";
import { useArgusAuth } from "../hooks/useArgusAuth";
import { useNavigate } from "react-router-dom";
import {
  fetchCartPriceData,
  fetchCartSummaryPriceData,
} from "../main-component/PageFunctions/priceFunctions";
import { fetchProductsBySkus } from "../main-component/PageFunctions/productFunctions";
import {
  createCheckout,
  fetchOrderCheckoutData,
} from "../main-component/PageFunctions/checkoutFunctions";
import { ApiCallStatus } from "../utils/constants";

const ArgusCartContext = createContext({});

export const ArgusCartProvider = ({ children }) => {
  const {
    addItem,
    removeItem,
    totalItems,
    items,
    setItems,
    emptyCart,
    clearCartMetadata,
    updateItemQuantity,
  } = useCart();

  const [prevSkus, setPrevSkus] = useState([]);

  const { isAuthenticated, isLoading } = useArgusAuth();
  const navigate = useNavigate();

  const goBack = () => navigate(-1);
  const gotoCheckoutPage = () => navigate("/checkout/summary");

  const [addedToCartViewOpen, setAddedToCartViewOpen] = useState(false);
  const [itemAddedToCart, setItemAddedToCart] = useState(null);
  const [productData, setProductData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [orderCheckoutInfo, setOrderCheckoutInfo] = useState(null);
  const [vat, setVat] = useState(null);
  const [cartSummaryPricingData, setCartSummaryPricingData] = useState([]);
  const [cartNettTotal, setCartNettTotal] = useState(0);
  const [settlementAmount, setSettlementAmount] = useState(0);
  const [cartVatAmount, setCartVatAmount] = useState(0);
  const [cartGrandTotal, setCartGrandTotal] = useState(0);

  const [orderCheckoutInfoCallStatus, setOrderCheckoutInfoCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [createCheckoutCallStatus, setCreateCheckoutCallStatus] = useState(
    ApiCallStatus.NotStarted
  );
  const [productDataCallStatus, setProductDataCallStatus] = useState(
    ApiCallStatus.NotStarted
  );
  const [cartPriceDataCallStatus, setCartPriceDataCallStatus] = useState(
    ApiCallStatus.NotStarted
  );
  const [cartSummaryPriceDataCallStatus, setCartSummaryPriceDataCallStatus] =
    useState(ApiCallStatus.NotStarted);

  //added a quantity parameter, which defaults to = 1 if not provided

  const handleAddToCart = ({ id, name, vendor, sku, image, quantity = 1 }) => {
    const cartProduct = {
      id: id,
      name: name,
      brand: vendor,
      sku: sku,
      price: 0,
      image: image,
    };
    //Pass the quantity to addItem instead of explicitly making it 1
    addItem(cartProduct, quantity);
    setItemAddedToCart(cartProduct);
    setAddedToCartViewOpen(true);

    ReactGA.event({
      category: "Ecommerce",
      action: "add_to_cart",
      label: sku,
      nonInteraction: false,
    });
  };

  const handleRemoveItem = (sku) => {
    removeItem(sku);

    ReactGA.event({
      category: "Ecommerce",
      action: "remove_from_cart",
      label: sku,
      nonInteraction: false,
    });
  };

  const handleQuantityUpdate = (id, quantity) => {
    updateItemQuantity(id, quantity);

    const item = find(items, (x) => x.id === id);
    let sku = item?.sku;

    ReactGA.event({
      category: "Ecommerce",
      action: "update_cart_quantity",
      label: sku,
      value: quantity,
      nonInteraction: false,
    });
  };

  const handleCloseAddedToCartView = () => {
    setAddedToCartViewOpen(false);
    setItemAddedToCart(null);
  };

  const fetchCheckoutOrderDataAsync = async () => {
    await fetchOrderCheckoutData({
      setOrderCheckoutInfo,
      setStatusInProgress: () =>
        setOrderCheckoutInfoCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setOrderCheckoutInfoCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setOrderCheckoutInfoCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setOrderCheckoutInfoCallStatus(ApiCallStatus.Error),
    });
  };

  const createCheckoutAsync = async () => {
    await createCheckout({
      items,
      setStatusInProgress: () =>
        setCreateCheckoutCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setCreateCheckoutCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () => setCreateCheckoutCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setCreateCheckoutCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchProductDataAsync = async () => {
    const productSkus = items?.map((p) => p.sku);
    await fetchProductsBySkus({
      productSkus,
      setProductData,
      setStatusInProgress: () =>
        setProductDataCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () => setProductDataCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () => setProductDataCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setProductDataCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchPricesDataAsync = async () => {
    const productSkus = productData?.map((p) => p.sku);
    await Promise.all([
      fetchCartPriceData({
        productSkus,
        setPricingData,
        setStatusInProgress: () =>
          setCartPriceDataCallStatus(ApiCallStatus.InProgress),
        setStatusSuccess: () =>
          setCartPriceDataCallStatus(ApiCallStatus.Succeeded),
        setStatusFailed: () => setCartPriceDataCallStatus(ApiCallStatus.Failed),
        setStatusError: () => setCartPriceDataCallStatus(ApiCallStatus.Error),
      }),
      fetchCartSummaryPriceData({
        productSkus,
        setCartSummaryPricingData,
        setStatusInProgress: () =>
          setCartSummaryPriceDataCallStatus(ApiCallStatus.InProgress),
        setStatusSuccess: () =>
          setCartSummaryPriceDataCallStatus(ApiCallStatus.Succeeded),
        setStatusFailed: () =>
          setCartSummaryPriceDataCallStatus(ApiCallStatus.Failed),
        setStatusError: () =>
          setCartSummaryPriceDataCallStatus(ApiCallStatus.Error),
      }),
    ]);
  };

  useEffect(() => {
    if (orderCheckoutInfo?.vat) {
      const vatTemp = orderCheckoutInfo.vat;
      setVat(vatTemp);
    }
  }, [orderCheckoutInfo?.vat]);

  useEffect(() => {
    if (createCheckoutCallStatus === ApiCallStatus.Succeeded) {
      gotoCheckoutPage();
    }
  }, [createCheckoutCallStatus]);

  useEffect(() => {
    if (productDataCallStatus === ApiCallStatus.Succeeded && !!productData) {
      fetchPricesDataAsync();
    }
  }, [productData, productDataCallStatus]);

  useEffect(() => {
    if (
      cartSummaryPriceDataCallStatus === ApiCallStatus.Succeeded &&
      !!cartSummaryPricingData
    ) {
      let newCartNettTotal = 0;
      let newSettlementAmount = 0;
      let newCartVatAmount = 0;
      let newCartGrandTotal = 0;

      if (!!items && cartSummaryPricingData.length > 0) {
        items.map((item) => {
          const skuPrices = find(
            cartSummaryPricingData,
            (p) => p.sku === item.sku
          );

          if (!!skuPrices) {
            newCartNettTotal += skuPrices.nettTotal * item.quantity;
            newSettlementAmount += skuPrices.settlementAmount * item.quantity;
            newCartVatAmount += skuPrices.vatAmount * item.quantity;
            newCartGrandTotal += skuPrices.total * item.quantity;
          }
        });

        setCartNettTotal(newCartNettTotal);
        setSettlementAmount(newSettlementAmount);
        setCartVatAmount(newCartVatAmount);
        setCartGrandTotal(newCartGrandTotal);
      }
    }
  }, [items, cartSummaryPricingData, cartSummaryPriceDataCallStatus]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchCheckoutOrderDataAsync();
    }
  }, [isAuthenticated, isLoading]);

  //  ALL LOADING STATES
  const allApiCalls = [
    productDataCallStatus,
    cartPriceDataCallStatus,
    cartSummaryPriceDataCallStatus,
    orderCheckoutInfoCallStatus,
    createCheckoutCallStatus,
  ];

  const isLoadingCart =
    allApiCalls.includes(ApiCallStatus.InProgress) || isLoading;

  useEffect(() => {
    // Skip only when: loading AND not authenticated
    if (isLoading && !isAuthenticated) return;

    // take all cart items and extract only their sku values.. these will be the the currentSkus
    // until there's new Skus, then they're 'previous' skus
    const currentSkus = items.map((i) => i.sku).sort();

    // If cart hydration produced invalid items (undefined SKU), do nothing
    // doing this to avoid API calls before cart fully loads 💡💡
    if (currentSkus.length !== items.length) {
      // Invalid cart items present → do NOT fetch yet
      return;
    }

    //check if there's a new SKU?
    const hasNewSku = currentSkus.some((sku) => !prevSkus.includes(sku));

    // only fetches product data if there's new Skus and the cart(items) is obviously not empty
    if (hasNewSku && items.length > 0) {
      setPrevSkus(currentSkus);
      fetchProductDataAsync(); // Only runs when NEW product added
    }

    // Handle empty cart (user removed everything)
    if (items.length === 0 && prevSkus.length > 0) {
      setPrevSkus([]);
      setProductData(null);
      setPricingData(null);
      setCartSummaryPricingData([]);
      setCartNettTotal(0);
      setSettlementAmount(0);
      setCartVatAmount(0);
      setCartGrandTotal(0);
    }
    // items dependency: Triggers the effect when cart adds, removes, or updates any item (we need to check SKUs).
    // prevSkus dependency: Needed to compare old vs new SKUs.. safe guard not to run whenever we remove items
    // isAuthenticated dependency: Re-runs when user logs in/out — may need fresh data to get accurate prices.
    // isLoading dependency: Pauses effect until Auth0 is ready — prevents errors during login loading.
  }, [items, prevSkus, isAuthenticated, isLoading]);

  return (
    <ArgusCartContext.Provider
      value={{
        // Cart
        handleAddToCart,
        removeItem: handleRemoveItem,
        updateItemQuantity: handleQuantityUpdate,
        items,
        setItems,
        emptyCart,
        clearCartMetadata,
        pricingData,
        productData,
        totalItems,
        cartNettTotal,
        settlementAmount,
        cartVatAmount,
        cartGrandTotal,

        // Checkout
        createCheckout: createCheckoutAsync,
        vat,

        // UI
        isLoading: isLoadingCart,
        addedToCartViewOpen,
        itemAddedToCart,
        handleCloseAddedToCartView,
        goBack,
      }}
    >
      {children}
    </ArgusCartContext.Provider>
  );
};

export const useArgusCart = () => useContext(ArgusCartContext);
